import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays, parseISO } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import RadiusMap from './RadiusMap';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { MapPin } from 'lucide-react';
import { fmtTime12 } from '../../utils/time';

const fmtDT = v => { try { return format(parseISO(v), 'd MMM yy h:mm a'); } catch { return v || '–'; } };

// A clock with no GPS fix has a NULL distance (and verified reads false by DEFAULT) — show it
// as neutral grey "No GPS", never as 0m/Inside or as a violation. Not clocked at all → '–'.
const dash = <span className="text-slate-400">–</span>;
const noGps = <span className="text-slate-400 text-xs">No GPS</span>;

const distCell = (v, row, radius, timeKey) => {
  if (!row?.[timeKey]) return dash;
  if (v == null) return noGps;
  const dist = parseFloat(v) || 0;
  const outside = dist > (radius || 400);
  return (
    <span className={clsx('font-medium text-xs', outside ? 'text-red-600' : 'text-green-600')}>
      {dist}m {outside ? '(Outside)' : '(Inside)'}
    </span>
  );
};

const boolBadge = (distKey, timeKey) => (v, row) => {
  if (!row?.[timeKey]) return dash;
  if (row[distKey] == null) return <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs font-medium">No GPS</span>;
  return v
    ? <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium">Outside</span>
    : <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">Inside</span>;
};

const verifiedCell = (distKey, timeKey) => (v, row) => {
  if (!row?.[timeKey]) return dash;
  if (v) return <span className="text-green-600 font-semibold" title={row[distKey] == null ? 'Verified (manual)' : 'Verified'}>✓</span>;
  if (row[distKey] == null) return noGps;
  return <span className="text-red-600 font-semibold" title="Outside geofence — not verified">✗</span>;
};

export default function RadiusReport() {
  const [data, setData]         = useState([]);
  const [radius, setRadius]     = useState(200);
  const [staff, setStaff]       = useState([]);
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart]   = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEnd]       = useState(format(new Date(), 'yyyy-MM-dd'));
  const [staffId, setStaffId]   = useState('');
  const [clientId, setClientId] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const pagination = usePagination(data);

  useEffect(() => {
    api.get('/staff').then(r => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : r.data?.clients || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (staffId)  p.set('staff_id', staffId);
    if (clientId) p.set('client_id', clientId);
    api.get(`/reports/radius?${p}`)
      .then(r => {
        setData(r.data?.rows || []);
        setRadius(r.data?.geofence_radius_m || 400);
        setSelectedId(null);
        pagination.reset();
      })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, staffId, clientId]);

  useEffect(() => { load(); }, [load]);

  const COLS = [
    { key: 'shift_date',          label: 'Shift Date' },
    { key: 'staff_name',          label: 'Staff Name' },
    { key: 'client_names',        label: 'Client(s)' },
    { key: 'shift_type',          label: 'Shift Type' },
    { key: 'start_time',          label: 'Shift Time',        render: fmtTime12 },
    { key: 'scheduled_address',   label: 'Scheduled Address' },
    { key: 'clock_in_time',       label: 'Clock In',          render: fmtDT },
    { key: 'clock_in_distance_m', label: 'In Distance',       render: (v, row) => distCell(v, row, radius, 'clock_in_time') },
    { key: 'clock_in_outside',    label: 'In Outside',        render: boolBadge('clock_in_distance_m', 'clock_in_time') },
    { key: 'clock_out_time',      label: 'Clock Out',         render: fmtDT },
    { key: 'clock_out_distance_m',label: 'Out Distance',      render: (v, row) => distCell(v, row, radius, 'clock_out_time') },
    { key: 'clock_out_outside',   label: 'Out Outside',       render: boolBadge('clock_out_distance_m', 'clock_out_time') },
    { key: 'clock_in_verified',   label: 'In Verified',       render: verifiedCell('clock_in_distance_m', 'clock_in_time') },
    { key: 'clock_out_verified',  label: 'Out Verified',      render: verifiedCell('clock_out_distance_m', 'clock_out_time') },
    { key: 'clock_in_reason',     label: 'Reason' },
  ];

  const selStaff  = staff.find(s => s.id === staffId);
  const selClient = clients.find(c => c.id === clientId);
  const chips = [
    { label: 'Date Range', value: `${startDate} — ${endDate}` },
    { label: 'Geofence Radius', value: `${radius}m` },
    ...(selStaff  ? [{ label: 'Staff',  value: `${selStaff.first_name} ${selStaff.last_name}`,   onRemove: () => setStaffId('') }]  : []),
    ...(selClient ? [{ label: 'Client', value: `${selClient.first_name} ${selClient.last_name}`, onRemove: () => setClientId('') }] : []),
  ];

  return (
    <ReportLayout title="Radius Report" data={data} filename="radius_report"
      columns={COLS} activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v => !v)}
      filters={
        <div className="flex flex-wrap gap-4">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Staff</label>
            <select value={staffId} onChange={e => setStaffId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Staff</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Client</label>
            <select value={clientId} onChange={e => setClientId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Clients</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select></div>
          <div className="flex items-end">
            <button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button>
          </div>
        </div>
      }>
      {data.length > 0 && (
        <>
          <div className="mb-4">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <MapPin size={15} className="text-primary-500" />
              <h2 className="text-sm font-semibold text-slate-700">Geofence location review</h2>
              <span className="text-xs text-slate-400">Click a pin or a table row to inspect that shift's geofence.</span>
            </div>
            <RadiusMap rows={data} radius={radius} selectedId={selectedId} onSelect={setSelectedId} />
          </div>
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
            Showing {data.length} clock-in/out event(s) outside the <strong>{radius}m geofence</strong>.
            Configure the geofence radius in Financials → Settings.
          </div>
        </>
      )}
      <ReportTable columns={COLS} data={data} loading={loading} pagination={pagination}
        emptyMsg="No radius violations found for this period."
        rowKey={(r) => r.shift_id} selectedRowKey={selectedId}
        onRowClick={(r) => setSelectedId(r.shift_id)} />
    </ReportLayout>
  );
}
